import { apiClient } from '@/utils/api';

// Interfaces
export interface FoodVendor {
  id: string;
  vendor_name: string;
  vendor_type: string;
  is_active: boolean;
  ordering_days: string[];
  cutoff_time: string | null;
  delivery_time: string | null;
  phone: string | null;
  website: string | null;
  notes: string | null;
}

export interface FoodMenuItem {
  id: string;
  item_name: string;
  category: string;
  description: string | null;
  price: number;
  is_available: boolean;
  customization_options: any;
  allergens: string[] | null;
  nutritional_info: any;
  image_url: string | null;
  sort_order: number;
}

export interface FoodOrderItem {
  item_id?: string;
  item_name: string;
  category?: string;
  quantity: number;
  price: number;
  customizations?: any;
}

export interface FoodOrder {
  id: string;
  order_date: string;
  delivery_date: string;
  vendor_name: string;
  vendor_type: string;
  order_items: FoodOrderItem[];
  total_amount: number;
  status: string;
  payment_status: string;
  special_instructions: string | null;
  is_favorite: boolean;
  created_at: string;
}

// API Functions
export const getVendors = async (activeOnly: boolean = true) => {
  const params = new URLSearchParams({ active_only: activeOnly.toString() });
  return apiClient.get(`/api/food-orders/vendors?${params}`);
};

export const getVendorMenu = async (vendorId: string, category?: string) => {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  return apiClient.get(`/api/food-orders/vendors/${vendorId}/menu?${params}`);
};

export const placeOrder = async (
  vendorId: string,
  orderItems: FoodOrderItem[],
  deliveryDate?: string,
  specialInstructions?: string
) => {
  return apiClient.post('/api/food-orders/orders/place', {
    vendor_id: vendorId,
    order_items: orderItems,
    delivery_date: deliveryDate,
    special_instructions: specialInstructions
  });
};

export const getMyOrders = async (limit: number = 10, offset: number = 0) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString()
  });
  return apiClient.get(`/api/food-orders/orders/my-orders?${params}`);
};

export const getFamilyOrders = async (date: string) => {
  return apiClient.get(`/api/food-orders/orders/family/${date}`);
};

export const getFavoriteOrders = async () => {
  return apiClient.get('/api/food-orders/orders/favorites');
};

export const toggleFavorite = async (orderId: string, isFavorite: boolean) => {
  return apiClient.put(`/api/food-orders/orders/${orderId}/favorite`, {
    is_favorite: isFavorite
  });
};

export const addMenuItem = async (
  vendorId: string,
  itemName: string,
  category: string,
  price: number,
  description?: string,
  customizationOptions?: any,
  allergens?: string[]
) => {
  return apiClient.post('/api/food-orders/menu-items', {
    vendor_id: vendorId,
    item_name: itemName,
    category,
    price,
    description,
    customization_options: customizationOptions,
    allergens
  });
};

export const updateMenuItem = async (itemId: string, updates: Partial<FoodMenuItem>) => {
  return apiClient.put(`/api/food-orders/menu-items/${itemId}`, updates);
};

// Admin vendor management functions
export const createVendor = async (vendorData: Partial<FoodVendor>) => {
  return apiClient.post('/api/food-orders/admin/vendors', vendorData);
};

export const updateVendor = async (vendorId: string, vendorData: Partial<FoodVendor>) => {
  return apiClient.put(`/api/food-orders/admin/vendors/${vendorId}`, vendorData);
};

export const deleteVendor = async (vendorId: string) => {
  return apiClient.delete(`/api/food-orders/admin/vendors/${vendorId}`);
};

export const createMenuItem = async (vendorId: string, menuItemData: Partial<FoodMenuItem>) => {
  return apiClient.post(`/api/food-orders/admin/vendors/${vendorId}/menu-items`, menuItemData);
};

export const adminUpdateMenuItem = async (itemId: string, menuItemData: Partial<FoodMenuItem>) => {
  return apiClient.put(`/api/food-orders/admin/menu-items/${itemId}`, menuItemData);
};

export const deleteMenuItem = async (itemId: string) => {
  return apiClient.delete(`/api/food-orders/admin/menu-items/${itemId}`);
};

export const getMenuItemStatistics = async (vendorId: string) => {
  return apiClient.get(`/api/food-orders/admin/vendors/${vendorId}/statistics`);
};